import axios, { AxiosRequestConfig } from 'axios';
import { config } from '../config';
import { getLocalStorageItem } from './localStorage';
import { Transaction, TokenTransaction } from './types';

// Define response types
interface AuthResponse {
  success: boolean;
  token?: string;
  email?: string;
  message?: string;
  user?: {
    role: string;
  };
  error?: string;
}

interface WalletResponse {
  success: boolean;
  tokenBalance?: number;
  message?: string;
  error?: string;
  status?: string;
  payment_status?: string;
  payment_intent?: string;
  customer_email?: string;
  tokensAdded?: number;
  newBalance?: number;
  alreadyProcessed?: boolean;
  code?: string;
  amount?: number; // Amount in cents from Stripe session
}

// Add Stripe response types
interface StripeAccountResponse {
  success: boolean;
  stripeConnected?: boolean;
  stripeAccountId?: string;
  accountId?: string;
  url?: string;
  message?: string;
  error?: string;
  statusCode?: number;
  details?: any;
  // Account details properties
  detailsSubmitted?: boolean;
  accountActive?: boolean;
  payoutsEnabled?: boolean;
  chargesEnabled?: boolean;
  country?: string | null;  // ISO 3166-1 alpha-2 country code
  capable?: boolean; // If account is capable of receiving payouts
  transfer_capability_status?: string; // Status of transfer capability
  // Test mode properties
  isTestMode?: boolean;
  testWarning?: string;
  // Error handling properties
  isPlatformError?: boolean;
}

/**
 * Response from burning tokens for Stripe account credit
 */
interface TokenBurnResponse {
  success: boolean;
  transferId?: string;
  chargeId?: string;
  transactionId?: string;
  tokens?: number;
  amount?: string | number;
  currency?: string;
  message?: string;
  error?: string;
  testMode?: boolean;
  details?: any;
}

// Add settings response type
interface UserSettingsResponse {
  success: boolean;
  settings?: {
    language: string;
    themeMode: string;
  };
  message?: string;
}

// Add transaction response type
interface TransactionHistoryResponse {
  success: boolean;
  transactions?: Transaction[];
  message?: string;
  error?: string;
}

/**
 * Token transactions response type
 */
interface TokenTransactionsResponse {
  success: boolean;
  transactions?: TokenTransaction[];
  message?: string;
  error?: string;
}

/**
 * Checkout session response type
 */
interface CheckoutSessionResponse {
  success: boolean;
  url?: string;
  id?: string;
  clientSecret?: string;
  sessionId?: string;
  message?: string;
  error?: string;
}

/**
 * Create axios instance with base URL
 */
const apiClient = axios.create({
  baseURL: config.api.baseUrl,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Add token to all requests that require auth
 */
apiClient.interceptors.request.use((config) => {
  const token = getLocalStorageItem<string>('authToken', '');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
});

/**
 * Handle 401 responses globally
 */
apiClient.interceptors.response.use(
  (response) => {
    // List of unauthenticated endpoints that should not trigger token refresh
    const unauthenticatedEndpoints = [
      '/api/auth/refresh-token',
      '/api/auth/login',
      '/api/auth/signup',
      '/api/auth/request-password-reset',
      '/api/auth/verify-reset-token',
      '/api/auth/reset-password'
    ];
    
    // Only refresh token if we have a token and this is not an unauthenticated endpoint
    const hasToken = getLocalStorageItem<string>('authToken', '');
    const isUnauthenticatedEndpoint = unauthenticatedEndpoints.some(endpoint => 
      response.config.url?.includes(endpoint)
    );
    
    if (hasToken && !isUnauthenticatedEndpoint) {
      api.auth.refreshToken().then(result => {
        if (result.success && result.token) {
          localStorage.setItem('authToken', result.token);
          document.cookie = `authToken=${result.token}; path=/;`;
        }
      }).catch(error => {
        console.error('Token refresh failed:', error);
      });
    }
    return response;
  },
  (error) => {
    // List of endpoints where 401 should not trigger redirect
    const publicEndpoints = [
      '/api/auth/request-password-reset',
      '/api/auth/verify-reset-token',
      '/api/auth/reset-password',
      '/api/auth/login',
      '/api/auth/signup'
    ];
    
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      error.config?.url?.includes(endpoint)
    );
    
    // Handle token expiration or unauthorized access (but not for public endpoints)
    if (error.response && error.response.status === 401 && !isPublicEndpoint) {
      // Clear all auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('userEmail');
      document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'userEmail=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * API client with typed methods for various endpoints
 */
export const api = {
  // Auth methods
  auth: {
    /**
     * Admin login with password
     * @param password Admin password
     * @returns Authentication response with token
     */
    login: async (password: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/admin-login', { password });
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Login failed. Please try again.'
        };
      }
    },
    
    /**
     * Verify token validity
     * @returns Response indicating if token is valid
     */
    verifyToken: async (): Promise<AuthResponse> => {
      try {
        const response = await apiClient.get('/api/auth/verify');
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Token verification failed.'
        };
      }
    },
    
    /**
     * User login with email and password
     * @param email User's email
     * @param password User's password
     * @returns Authentication response with token
     */
    userLogin: async (email: string, password: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/login', { email, password });
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Login failed. Please try again.'
        };
      }
    },
    
    /**
     * Register a new user with email and password
     * @param email User's email
     * @param password User's password
     * @returns Authentication response with token
     */
    signup: async (email: string, password: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/signup', { email, password });
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Signup failed. Please try again.'
        };
      }
    },
    
    /**
     * Request password reset
     * @param email User's email
     * @returns Response indicating if the request was successful
     */
    requestPasswordReset: async (email: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/request-password-reset', { email });
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Password reset request failed. Please try again.'
        };
      }
    },
    
    /**
     * Verify password reset token
     * @param token Reset token from email
     * @returns Response indicating if the token is valid
     */
    verifyResetToken: async (token: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.get(`/api/auth/verify-reset-token?token=${token}`);
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Invalid or expired token.'
        };
      }
    },
    
    /**
     * Reset password with token
     * @param token Reset token from email
     * @param password New password
     * @returns Response indicating if the password was reset successfully
     */
    resetPassword: async (token: string, password: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/reset-password', { token, password });
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Password reset failed. Please try again.'
        };
      }
    },
    
    /**
     * Verify current password
     * @param password Current password to verify
     * @returns Response indicating if the password is correct
     */
    verifyPassword: async (password: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/verify-password', { password });
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Password verification failed.'
        };
      }
    },
    
    /**
     * Change user password
     * @param currentPassword Current password
     * @param newPassword New password
     * @returns Response indicating if the password was changed successfully
     */
    changePassword: async (currentPassword: string, newPassword: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/change-password', { 
          currentPassword, 
          newPassword 
        });
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Password change failed. Please try again.'
        };
      }
    },
    
    /**
     * Verify admin password to grant admin role to the current user
     * @param password Admin password to verify
     * @returns Response indicating if the password was verified successfully
     */
    verifyAdminPassword: async (password: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/verify-admin-password', { password });
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Admin password verification failed.'
        };
      }
    },
    
    /**
     * Update user's email address with verification
     * @param email New email address
     * @param password Current password for verification
     * @returns Response indicating if the email change request was successful
     */
    updateEmail: async (email: string, password: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/request-email-change', { 
          newEmail: email, 
          password 
        });
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Email change request failed. Please try again.'
        };
      }
    },
    
    /**
     * Verify email change with token from email
     * @param token Email verification token
     * @returns Response indicating if the email was updated successfully
     */
    verifyEmailChange: async (token: string): Promise<AuthResponse> => {
      try {
        const response = await apiClient.get(`/api/auth/verify-email-change?token=${token}`);
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Email verification failed. Please try again.'
        };
      }
    },
    
    /**
     * Refresh the current token
     * @returns Response with new token
     */
    refreshToken: async (): Promise<AuthResponse> => {
      try {
        const response = await apiClient.post('/api/auth/refresh-token');
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Token refresh failed.'
        };
      }
    },
  },
  
  // User settings methods
  user: {
    /**
     * Get user settings from server
     * @param email User's email
     * @returns Response with user settings
     */
    getSettings: async (email: string): Promise<UserSettingsResponse> => {
      try {
        const response = await apiClient.get(`/api/user/${encodeURIComponent(email)}/settings`);
        return response.data;
      } catch (error) {
        return {
          success: false,
          message: 'Failed to retrieve settings'
        };
      }
    },
    
    /**
     * Update user settings on server
     * @param email User's email
     * @param settings Settings to update
     * @returns Response with updated settings
     */
    updateSettings: async (email: string, settings: {
      language?: string;
      themeMode?: string;
    }): Promise<UserSettingsResponse> => {
      try {
        const response = await apiClient.put(
          `/api/user/${encodeURIComponent(email)}/settings`,
          settings
        );
        return response.data;
      } catch (error) {
        return {
          success: false,
          message: 'Failed to update settings'
        };
      }
    }
  },
  
  // Wallet methods
  wallet: {
    /**
     * Get user token balance
     * @returns Wallet response with token balance
     */
    getTokenBalance: async (): Promise<WalletResponse> => {
      try {
        const response = await apiClient.get(`/api/wallet/me/balance`);
        return response.data;
      } catch (error: any) {
        // Handle specific authentication errors
        if (error.response && error.response.status === 401) {
          return {
            success: false,
            message: 'Authentication required',
            error: 'AUTH_REQUIRED'
          };
        }
        
        // Handle other errors
        return {
          success: false,
          message: 'Failed to retrieve balance',
          error: 'SERVER_ERROR'
        };
      }
    },
    
    /**
     * Add tokens to user wallet (admin action)
     * @param amount Amount of tokens to add
     * @returns Wallet response with updated balance
     */
    addTokens: async (amount: number): Promise<WalletResponse> => {
      try {
        // NOTE: This endpoint is now /me/add. Verify if this admin action still makes sense via /me/
        // If this is strictly an admin action, it might need a different endpoint/security
        const response = await apiClient.post(`/api/wallet/me/add`, { amount });
        return response.data;
      } catch (error: any) {
        // Simplified error handling (removed retry logic)
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Failed to add tokens. Please try again.'
        };
      }
    },

    /**
     * Create a Stripe checkout session
     * @param tokenAmount Amount of tokens to purchase
     * @param priceAmount Price in USD
     * @param successUrl Optional URL to redirect to on success (defaults to purchase-result)
     * @param userEmail Email of the user making the purchase (overrides stored email)
     * @returns Response with checkout session URL
     */
    createCheckoutSession: async (
      tokenAmount: string | number,
      priceAmount: string | number,
      successUrl?: string,
      userEmail?: string // Optional email for metadata
    ): Promise<CheckoutSessionResponse> => {
      try {
        console.log(`Creating checkout session using endpoint: /api/wallet/checkout-session for ${userEmail || 'unknown user'}`); // Log corrected endpoint
        // Use the correct backend endpoint path
        const response = await apiClient.post('/api/wallet/checkout-session', {
          tokenAmount,
          priceAmount,
          successUrl: successUrl || window.location.origin + '/purchase-result?session_id={CHECKOUT_SESSION_ID}', // More robust default success URL
          userEmail // Pass email if provided
        });
        return response.data;
      } catch (error: any) {
        console.error('Create checkout session error:', error.response?.data || error.message); // Log error detail
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Failed to create checkout session'
        };
      }
    },

    /**
     * Get checkout session status by session ID
     * @param sessionId Stripe checkout session ID
     * @returns Wallet response with session status
     */
    getSessionStatus: async (sessionId: string): Promise<WalletResponse> => {
      try {
        console.log(`Checking status for session: ${sessionId}`);
        const response = await apiClient.get(`/api/wallet/session-status?session_id=${sessionId}`);
        return response.data;
      } catch (error: any) {
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Failed to get session status',
          error: 'REQUEST_FAILED'
        };
      }
    },

    /**
     * Get the Stripe Connect account status for a user
     * @param email User's email
     * @returns Stripe account status
     */
    getStripeAccountStatus: async (email: string): Promise<StripeAccountResponse> => {
      if (!email) {
        return {
          success: false,
          message: 'Email is required'
        };
      }
      
      try {
        const response = await apiClient.get(`/api/wallet/${encodeURIComponent(email)}/stripe-status`);
        return response.data;
      } catch (error: any) {
        console.error('Error getting Stripe account status:', error);
        return {
          success: false,
          message: 'Failed to get Stripe account status',
          error: error.message
        };
      }
    },

    /**
     * Check payout capability for a Stripe Connect account
     * @param email User's email
     * @returns Payout capability status
     */
    checkPayoutCapability: async (email: string): Promise<{
      success: boolean,
      capable?: boolean,
      accountId?: string,
      stripeAccountId?: string,
      detailsSubmitted?: boolean,
      details_submitted?: boolean,
      payoutsEnabled?: boolean,
      payouts_enabled?: boolean,
      chargesEnabled?: boolean,
      charges_enabled?: boolean,
      transfer_capability_status?: string,
      message?: string,
      error?: string
    }> => {
      if (!email) {
        return {
          success: false,
          message: 'Email is required'
        };
      }
      
      try {
        const response = await apiClient.get(`/api/wallet/${encodeURIComponent(email)}/payout-capability`);
        return response.data;
      } catch (error: any) {
        console.error('Error checking payout capability:', error);
        return {
          success: false,
          message: 'Failed to check payout capability',
          error: error.message
        };
      }
    },

    /**
     * Create a dashboard link for a Stripe Connect account
     * @param email User's email
     * @returns Dashboard link URL
     */
    createDashboardLink: async (email: string): Promise<{
      success: boolean,
      url?: string,
      message?: string,
      error?: string
    }> => {
      if (!email) {
        return {
          success: false,
          message: 'Email is required'
        };
      }
      
      try {
        const response = await apiClient.get(`/api/wallet/${encodeURIComponent(email)}/dashboard-link`);
        return response.data;
      } catch (error: any) {
        console.error('Error creating dashboard link:', error);
        return {
          success: false,
          message: 'Failed to create dashboard link',
          error: error.message
        };
      }
    },

    /**
     * Create a Stripe Connect account for a user
     * @param email User's email
     * @param country User's country as an ISO 3166-1 alpha-2 country code (e.g., "US" for United States)
     * @param returnUrl Optional custom return URL for the Stripe onboarding process
     * @returns Account creation result with onboarding URL
     */
    createStripeAccount: async (email: string, country?: string, returnUrl?: string): Promise<StripeAccountResponse> => {
      if (!email) {
        return {
          success: false,
          message: 'Email is required'
        };
      }
      
      try {
        console.log(`Requesting Stripe account creation for: ${email}`);
        const response = await apiClient.post(`/api/wallet/${encodeURIComponent(email)}/connect-account`, 
          { 
            country,
            returnUrl
          }
        );
        return response.data;
      } catch (error: any) {
        console.error('Error creating Stripe account:', error);
        
        // Check if the response contains HTML (usually an error page)
        if (error.response?.data && typeof error.response.data === 'string' && 
            error.response.data.includes('<!DOCTYPE')) {
          console.error('Received HTML error page instead of JSON response');
          return {
            success: false,
            message: 'Server returned an HTML error page. The API server might be experiencing issues or is misconfigured.'
          };
        }
        
        // Return a properly formatted error response with diagnostics
        return {
          success: false,
          message: error.response?.data?.message || error.message || 'Failed to create Stripe account',
          error: error.response?.data?.error || error.code,
          statusCode: error.response?.status,
          details: error.response?.data || 'No additional details available'
        };
      }
    },

    /**
     * Burns tokens and credits the user's Stripe Connect account
     * Note: The function name remains burnTokensForCredit for backward compatibility,
     * but we use "exchange tokens" in user-facing text
     * 
     * @param email User's email
     * @param amount Optional amount of tokens to exchange. If not provided, exchanges all tokens.
     * @returns Token exchange result
     */
    burnTokensForCredit: async (email: string, amount?: number): Promise<TokenBurnResponse> => {
      if (!email) {
        return {
          success: false,
          message: 'Email is required'
        };
      }
      
      try {
        // Add a test bypass parameter in development environments
        let url = `/api/wallet/${encodeURIComponent(email)}/burn-tokens`;
        if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
          url += '?test_bypass=true';
        }
        
        // Always send the amount in the request body
        const payload = { amount: amount || 0 };
        
        const response = await apiClient.post(url, payload);
        return response.data;
      } catch (error: any) {
        console.error('Error burning tokens for credit:', error);
        
        // Provide a more helpful error message based on the server's response
        let errorMessage = 'Failed to burn tokens for credit';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        return {
          success: false,
          message: errorMessage,
          error: error.message,
          details: error.response?.data
        };
      }
    },

    /**
     * Get purchase transaction history
     * @param timeframe Optional timeframe filter ('all', 'today', '7days')
     * @returns Response with transaction history
     */
    getTransactionHistory: async (
      timeframe?: string
    ): Promise<TransactionHistoryResponse> => {
      try {
        const params: { timeframe?: string } = {};
        if (timeframe) {
          params.timeframe = timeframe;
        }
        const response = await apiClient.get(`/api/wallet/me/transactions`, { params });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching transaction history:', error);
        if (error.response?.data) {
          return error.response.data;
        }
        return {
          success: false,
          message: 'Failed to fetch transaction history'
        };
      }
    },

    /**
     * Get all token transaction history (purchases, taps, etc.)
     * @param timeframe Optional timeframe filter ('all', 'today', '7days')
     * @returns Response with token transaction history
     */
    getTokenTransactions: async (
      timeframe?: string
    ): Promise<TokenTransactionsResponse> => {
      try {
        const params: { timeframe?: string } = {};
        if (timeframe) {
          params.timeframe = timeframe;
        }
        const response = await apiClient.get(`/api/wallet/me/token-transactions`, { params });
        return response.data;
      } catch (error: any) {
        console.error('Error fetching token transactions:', error);
        if (error.response?.data) {
          return error.response.data;
        }
        
        // Return a more descriptive error based on status code
        if (error.response?.status === 401 || error.response?.status === 403) {
          return {
            success: false,
            message: 'Authentication required. Please log in again.'
          };
        }
        
        return {
          success: false,
          message: 'Failed to fetch token transactions'
        };
      }
    },
  },
  
  // Counters methods
  counters: {
    getAll: async () => {
      const response = await apiClient.get('/api/counters');
      return response.data;
    },
    create: async (name: string) => {
      const response = await apiClient.post('/api/counters', { name });
      return response.data;
    },
    update: async (id: string, data: any) => {
      const response = await apiClient.put(`/api/counters/${id}`, data);
      return response.data;
    },
    delete: async (id: string) => {
      const response = await apiClient.delete(`/api/counters/${id}`);
      return response.data;
    },
  },
  
  // Performance methods
  performance: {
    getStats: async (params?: any) => {
      const response = await apiClient.get('/api/performance/metrics', { params });
      return response.data;
    },
    resetStats: async () => {
      const response = await apiClient.post('/api/performance/metrics/reset');
      return response.data;
    },
    runTest: async (params: any) => {
      const response = await apiClient.post('/api/performance/test', params);
      return response.data;
    },
  },
}; 